import { PAIR_MARKETS, SUBJECT_MARKETS, isOutrightMarket, sameOutcome, type Outcome } from '../engine/markets.js'
import { currentOdds, type QuotedMarket } from '../engine/quote.js'
import { countColumns } from './columns.js'
import type { SlipType } from './types.js'

export type SlipSelectionDraft = Outcome & { odds: number }
export type SlipLegDraft = { matchId?: string | null; banker?: boolean; selections: readonly SlipSelectionDraft[] }
export type SlipDraft = {
  type: SlipType
  systemSizes?: readonly number[]
  stakePerColumn: number
  legs: readonly SlipLegDraft[]
}
export type SlipLimits = { maxLegs: number; maxColumns: number; maxBet: number | null }
export type SelectionRef = { leg: number; selection: number }
export type SlipShapeError = 'invalidSlip' | 'sameMatchTwice' | 'duplicateSelection' | 'oppositeSelections' | 'missingSubject' | 'tooManyLegs' | 'tooManyColumns' | 'overMaxBet'
export type SlipCheck =
  | { ok: true; columns: number; totalStake: number }
  | { ok: false; code: SlipShapeError | 'ownMatch' }
  | { ok: false; code: 'marketClosed'; closed: SelectionRef[] }
  | { ok: false; code: 'oddsChanged'; changed: (SelectionRef & { odds: number })[] }
export type SlipContext = {
  limits: SlipLimits
  /** Mercati quotati di un evento per `legKey`: una partita (undefined se sconosciuta o conclusa) o i mercati antepost. */
  quotes: (legKey: string) => readonly QuotedMarket[] | undefined
  teamsOf: (matchId: string) => { homeTeamId: string; awayTeamId: string } | undefined
  /** Squadra di chi gioca (null se ancora senza squadra). */
  teamId: string | null
}

/** Prefisso della chiave di un evento antepost: l'evento è il suo mercato, al massimo uno per mercato (spec 4). */
export const OUTRIGHT_LEG_PREFIX = 'outright:'
/** Chiave di un evento: la partita, o `outright:<mercato>` per un antepost (senza partita). Validazione, quote e schedina usano questa. */
export const legKey = (leg: { matchId?: string | null; selections: readonly { market: string }[] }): string =>
  leg.matchId ?? `${OUTRIGHT_LEG_PREFIX}${leg.selections[0]?.market ?? ''}`

/** Un antepost ha esiti di un solo mercato antepost; una partita non ha esiti antepost. */
const misplaced = (leg: SlipLegDraft) =>
  leg.matchId
    ? leg.selections.some((s) => isOutrightMarket(s.market))
    : leg.selections.some((s) => !isOutrightMarket(s.market) || s.market !== leg.selections[0].market)

/** Opposti: stesso mercato a due vie, stessa linea e stesso giocatore, scelta diversa (1 e 2, over e under, sì e no). */
const opposite = (a: Outcome, b: Outcome) =>
  PAIR_MARKETS.has(a.market) && a.market === b.market && (a.line ?? null) === (b.line ?? null) && (a.subjectId ?? null) === (b.subjectId ?? null) && a.selection !== b.selection

/**
 * Forma e limiti (spec 4). Un evento è una partita o un mercato antepost: un antepost ha gli esiti di un solo mercato antepost, al massimo un evento per partita o mercato antepost. Singola: un evento e un esito. Multipla: almeno 2 eventi, un esito ciascuno, nessuna fissa.
 * Sistema: ordini 1 ≤ k ≤ eventi non fissi e almeno 2 colonne (anche una sola partita con due esiti = due singole).
 */
export function checkShape(slip: SlipDraft, limits: SlipLimits): { ok: true; columns: number; totalStake: number } | { ok: false; code: SlipShapeError } {
  const fail = (code: SlipShapeError) => ({ ok: false as const, code })
  const { legs } = slip
  if (legs.length === 0 || legs.some((l) => l.selections.length === 0) || legs.some(misplaced)) return fail('invalidSlip')
  if (new Set(legs.map(legKey)).size !== legs.length) return fail('sameMatchTwice')
  for (const leg of legs) {
    for (const [i, a] of leg.selections.entries()) {
      if (SUBJECT_MARKETS.has(a.market) && !a.subjectId) return fail('missingSubject')
      for (const b of leg.selections.slice(i + 1)) {
        if (sameOutcome(a, b)) return fail('duplicateSelection')
        if (opposite(a, b)) return fail('oppositeSelections')
      }
    }
  }
  const hasBanker = legs.some((l) => l.banker)
  if (slip.type === 'single' && (legs.length !== 1 || legs[0].selections.length !== 1 || hasBanker)) return fail('invalidSlip')
  if (slip.type === 'multiple' && (legs.length < 2 || legs.some((l) => l.selections.length !== 1) || hasBanker)) return fail('invalidSlip')
  if (legs.length > limits.maxLegs) return fail('tooManyLegs')
  if (slip.type === 'system') {
    const free = legs.filter((l) => !l.banker).length
    const sizes = slip.systemSizes ?? []
    if (sizes.length === 0 || sizes.some((k) => !Number.isInteger(k) || k < 1 || k > free)) return fail('invalidSlip')
  }
  const columns = countColumns(slip)
  if (slip.type === 'system' && columns < 2) return fail('invalidSlip')
  if (columns > limits.maxColumns) return fail('tooManyColumns')
  const totalStake = columns * slip.stakePerColumn
  if (limits.maxBet !== null && totalStake > limits.maxBet) return fail('overMaxBet')
  return { ok: true, columns, totalStake }
}

/** Forma, poi propria partita, poi esiti non offerti, poi quote cambiate: in quest'ordine, come gli errori della giocata. */
export function validateSlip(slip: SlipDraft, ctx: SlipContext): SlipCheck {
  const shape = checkShape(slip, ctx.limits)
  if (!shape.ok) return shape
  const own = slip.legs.some((leg) => {
    // Antepost: aperti a tutti, anche su sé stessi (spec 4).
    if (!leg.matchId) return false
    const teams = ctx.teamsOf(leg.matchId)
    return ctx.teamId !== null && teams !== undefined && (teams.homeTeamId === ctx.teamId || teams.awayTeamId === ctx.teamId)
  })
  if (own) return { ok: false, code: 'ownMatch' }
  const closed: SelectionRef[] = []
  const changed: (SelectionRef & { odds: number })[] = []
  slip.legs.forEach((leg, l) =>
    leg.selections.forEach((selection, s) => {
      const odds = currentOdds(ctx.quotes(legKey(leg)), selection)
      if (odds === null) closed.push({ leg: l, selection: s })
      else if (odds !== selection.odds) changed.push({ leg: l, selection: s, odds })
    }),
  )
  if (closed.length > 0) return { ok: false, code: 'marketClosed', closed }
  if (changed.length > 0) return { ok: false, code: 'oddsChanged', changed }
  return shape
}
